import type {
  ResearchReportStatus,
  SeoGeneratedComponentStatus,
  SeoGeneratedComponents,
} from './types'
import {
  getEffectiveSeoGeneratedArtifactStatus,
  isSeoGeneratedArtifactProcessingStatus,
  type SeoGeneratedArtifactLifecycle,
} from './lifecycle'
import { SEO_META_PROMPT_VERSION } from './meta-description'

const QUEUED_STALE_MS = 5 * 60 * 1000
const GENERATING_STALE_MS = 15 * 60 * 1000

function toTimestampMs(value: string | Date | null | undefined): number | null {
  if (value == null) return null
  const date = value instanceof Date ? value : new Date(value)
  const time = date.getTime()
  return Number.isFinite(time) ? time : null
}

function isStaleByThreshold(args: {
  status: 'queued' | 'generating'
  updatedAt: string | Date | null | undefined
  now?: number
}): boolean {
  const updatedAtMs = toTimestampMs(args.updatedAt)
  if (updatedAtMs == null) return false

  const ageMs = (args.now ?? Date.now()) - updatedAtMs
  const thresholdMs =
    args.status === 'queued' ? QUEUED_STALE_MS : GENERATING_STALE_MS

  return ageMs >= thresholdMs
}

export function isSeoReportProcessingStatus(
  status: ResearchReportStatus | string | null | undefined
): status is 'queued' | 'generating' {
  return status === 'queued' || status === 'generating'
}

export function isSeoGeneratedComponentProcessingStatus(
  status: SeoGeneratedComponentStatus | string | null | undefined
): status is 'queued' | 'generating' {
  return isSeoGeneratedArtifactProcessingStatus(status)
}

export function isSeoReportProcessingStale(args: {
  status: ResearchReportStatus | string | null | undefined
  updatedAt: string | Date | null | undefined
  now?: number
}): boolean {
  if (!isSeoReportProcessingStatus(args.status)) return false
  return isStaleByThreshold({
    status: args.status,
    updatedAt: args.updatedAt,
    now: args.now,
  })
}

export function getEffectiveSeoReportStatus(args: {
  status: ResearchReportStatus
  updatedAt: string | Date | null | undefined
  now?: number
}): ResearchReportStatus {
  if (isSeoReportProcessingStale(args)) {
    return 'failed'
  }

  return args.status
}

export function getEffectiveSeoGeneratedComponentStatus(args: {
  status: SeoGeneratedComponentStatus | null | undefined
  updatedAt: string | Date | null | undefined
  now?: number
}): SeoGeneratedComponentStatus | null {
  return getEffectiveSeoGeneratedArtifactStatus({
    artifact: { status: args.status },
    fallbackUpdatedAt: args.updatedAt,
    now: args.now,
  })
}

function getEffectiveSeoGeneratedComponentArtifactStatus(args: {
  component: SeoGeneratedArtifactLifecycle | null | undefined
  fallbackUpdatedAt: string | Date | null | undefined
  now?: number
  staleWhenProcessingWithoutTimestamp?: boolean
}) {
  return getEffectiveSeoGeneratedArtifactStatus({
    artifact: args.component,
    fallbackUpdatedAt: args.fallbackUpdatedAt,
    now: args.now,
    staleWhenProcessingWithoutTimestamp:
      args.staleWhenProcessingWithoutTimestamp,
  })
}

export function getSeoReportStaleProcessingMessage(
  status: 'queued' | 'generating'
) {
  return status === 'queued'
    ? 'Report generation never started after being queued. Re-run publish to try again.'
    : 'Report generation appears to have stalled. Re-run publish to try again.'
}

// ---------------------------------------------------------------------------
// Derived display model
// ---------------------------------------------------------------------------

export type SeoReportDisplayStatus =
  | 'draft'
  | 'processing'
  | 'live'
  | 'failed'
  | 'archived'

export interface SeoReportDisplayModel {
  primary: SeoReportDisplayStatus
  isEnriching: boolean
  hasComponentFailures: boolean
  staleProcessing: boolean
}

export type SeoReportPublicationReadinessState =
  | 'publishable'
  | 'processing'
  | 'needs_attention'

export type SeoReportPublicationGate =
  | 'document'
  | 'category'
  | 'image_check'
  | 'hero_image'
  | 'quick_answer'
  | 'who_its_for'
  | 'top_pick_descriptors'
  | 'product_quotes'
  | 'how_we_picked'
  | 'faq'
  | 'seo_title'
  | 'meta_description'

export interface SeoReportPublicationReadiness {
  state: SeoReportPublicationReadinessState
  processingGates: SeoReportPublicationGate[]
  failedGates: SeoReportPublicationGate[]
  missingGates: SeoReportPublicationGate[]
  upgradeGates: SeoReportPublicationGate[]
  reasons: string[]
}

const REQUIRED_COMPONENT_GATES = [
  'image_check',
  'hero_image',
  'quick_answer',
  'who_its_for',
  'top_pick_descriptors',
  'product_quotes',
  // social_signals and visual_signals are optional enrichment, so they are
  // admin-visible/regenerable but do not gate publication readiness.
  'how_we_picked',
  'faq',
  'seo_title',
  'meta_description',
] as const satisfies Array<keyof SeoGeneratedComponents>

const PUBLICATION_GATE_LABELS: Record<SeoReportPublicationGate, string> = {
  document: 'Report document',
  category: 'Category',
  image_check: 'Image check',
  hero_image: 'Hero image',
  quick_answer: 'Quick answer',
  who_its_for: 'Who this is for',
  top_pick_descriptors: 'Top pick descriptors',
  product_quotes: 'Product quotes',
  how_we_picked: 'How we picked',
  faq: 'FAQ',
  seo_title: 'SEO title',
  meta_description: 'Meta description',
}

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function hasArrayItems(value: unknown): value is unknown[] {
  return Array.isArray(value) && value.length > 0
}

type RequiredComponentGate = (typeof REQUIRED_COMPONENT_GATES)[number]

type RequiredComponentContract = {
  // `renderable` is the compatibility contract for already-public reports.
  // It should accept old-but-safe payloads so prompt/component changes do not
  // remove live reports from public visibility.
  hasRenderablePayload: (
    component: SeoGeneratedComponents[RequiredComponentGate],
    components: SeoGeneratedComponents
  ) => boolean
  // `current` is the publish contract for new/internal reports. Tighten this
  // when a generated section gains a new required field or prompt output.
  hasCurrentPayload: (
    component: SeoGeneratedComponents[RequiredComponentGate],
    components: SeoGeneratedComponents
  ) => boolean
}

function isReadyComponent(
  component: { status?: SeoGeneratedComponentStatus | null } | null | undefined
) {
  return component?.status === 'ready'
}

function hasRenderableImageCheckPayload(
  component: SeoGeneratedComponents[RequiredComponentGate],
  components: SeoGeneratedComponents
) {
  const imageCheck = component as SeoGeneratedComponents['image_check']
  if (hasArrayItems(imageCheck?.products)) return true

  const hero = components.hero_image
  return hasText(hero?.assetUrl)
}

function hasCurrentImageCheckPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const imageCheck = component as SeoGeneratedComponents['image_check']
  return isReadyComponent(imageCheck) && hasArrayItems(imageCheck?.products)
}

function hasRenderableHeroImagePayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const heroImage = component as SeoGeneratedComponents['hero_image']
  return hasText(heroImage?.assetUrl)
}

function hasCurrentHeroImagePayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const heroImage = component as SeoGeneratedComponents['hero_image']
  return isReadyComponent(heroImage) && hasText(heroImage?.assetUrl)
}

function hasRenderableQuickAnswerPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const quickAnswer = component as SeoGeneratedComponents['quick_answer']
  return (
    hasText(quickAnswer?.content?.title) && hasText(quickAnswer?.content?.body)
  )
}

function hasCurrentQuickAnswerPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const quickAnswer = component as SeoGeneratedComponents['quick_answer']
  // Legacy payloads carry the removed fabricated "Dupe activity" stat. They
  // stay renderable (the projection drops the stat before it reaches the
  // page) but are flagged as upgrade targets so regeneration purges the
  // fabricated data from storage.
  const legacyContent = quickAnswer?.content as
    | (NonNullable<
        NonNullable<SeoGeneratedComponents['quick_answer']>['content']
      > & { dupeActivityVerdict?: unknown })
    | null
    | undefined
  return (
    isReadyComponent(quickAnswer) &&
    hasRenderableQuickAnswerPayload(quickAnswer) &&
    legacyContent?.dupeActivityVerdict == null
  )
}

function hasRenderableWhoItsForPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const whoItsFor = component as SeoGeneratedComponents['who_its_for']
  return hasText(whoItsFor?.content?.body)
}

function hasCurrentWhoItsForPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const whoItsFor = component as SeoGeneratedComponents['who_its_for']
  return isReadyComponent(whoItsFor) && hasRenderableWhoItsForPayload(whoItsFor)
}

function hasRenderableTopPickDescriptorsPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const descriptors =
    component as SeoGeneratedComponents['top_pick_descriptors']
  return hasArrayItems(descriptors?.descriptors)
}

function hasCurrentTopPickDescriptorsPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const descriptors =
    component as SeoGeneratedComponents['top_pick_descriptors']
  return (
    isReadyComponent(descriptors) &&
    hasRenderableTopPickDescriptorsPayload(descriptors)
  )
}

function hasRenderableProductQuotesPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const productQuotes = component as SeoGeneratedComponents['product_quotes']
  // Legacy public reports predate product_quotes and can render product cards
  // from canonical/social evidence fallbacks. A present component remains
  // renderable through its first regeneration attempt or whenever it carries
  // the old per-product payload shape.
  return (
    productQuotes == null ||
    productQuotes.generatedAt == null ||
    hasArrayItems(productQuotes?.content?.products)
  )
}

function hasCurrentProductQuotesPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const productQuotes = component as SeoGeneratedComponents['product_quotes']
  // Ready means the generation produced at least one per-product entry.
  // Individual products may legitimately have an empty `quotes` array if no
  // direct evidence was found.
  return (
    isReadyComponent(productQuotes) &&
    hasArrayItems(productQuotes?.content?.products)
  )
}

function hasRenderableHowWePickedPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const howWePicked = component as SeoGeneratedComponents['how_we_picked']
  return (
    hasText(howWePicked?.content?.intro) &&
    hasArrayItems(howWePicked?.content?.criteria)
  )
}

function hasCurrentHowWePickedPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const howWePicked = component as SeoGeneratedComponents['how_we_picked']
  return (
    isReadyComponent(howWePicked) &&
    hasRenderableHowWePickedPayload(howWePicked)
  )
}

function hasRenderableFaqPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const faq = component as SeoGeneratedComponents['faq']
  return hasArrayItems(faq?.items)
}

function hasCurrentFaqPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const faq = component as SeoGeneratedComponents['faq']
  return isReadyComponent(faq) && hasRenderableFaqPayload(faq)
}

function hasRenderableSeoTitlePayload() {
  return true
}

function hasCurrentSeoTitlePayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const seoTitle = component as SeoGeneratedComponents['seo_title']
  return isReadyComponent(seoTitle) && hasText(seoTitle?.content?.text)
}

function hasRenderableMetaDescriptionPayload() {
  return true
}

function hasCurrentMetaDescriptionPayload(
  component: SeoGeneratedComponents[RequiredComponentGate]
) {
  const metaDescription =
    component as SeoGeneratedComponents['meta_description']
  // Descriptions written by the retired template prompt (meta-v2 and
  // earlier) stay renderable but are flagged as upgrade targets: the v2
  // corpus was 100% template-identical, and regeneration replaces it with
  // bespoke, uniqueness-gated copy (see meta-description.ts).
  return (
    isReadyComponent(metaDescription) &&
    hasText(metaDescription?.content?.text) &&
    metaDescription?.promptVersion === SEO_META_PROMPT_VERSION
  )
}

const GENERATED_COMPONENT_PAYLOAD_CONTRACTS: Record<
  RequiredComponentGate,
  RequiredComponentContract
> = {
  image_check: {
    hasRenderablePayload: hasRenderableImageCheckPayload,
    hasCurrentPayload: hasCurrentImageCheckPayload,
  },
  hero_image: {
    hasRenderablePayload: hasRenderableHeroImagePayload,
    hasCurrentPayload: hasCurrentHeroImagePayload,
  },
  quick_answer: {
    hasRenderablePayload: hasRenderableQuickAnswerPayload,
    hasCurrentPayload: hasCurrentQuickAnswerPayload,
  },
  who_its_for: {
    hasRenderablePayload: hasRenderableWhoItsForPayload,
    hasCurrentPayload: hasCurrentWhoItsForPayload,
  },
  top_pick_descriptors: {
    hasRenderablePayload: hasRenderableTopPickDescriptorsPayload,
    hasCurrentPayload: hasCurrentTopPickDescriptorsPayload,
  },
  product_quotes: {
    hasRenderablePayload: hasRenderableProductQuotesPayload,
    hasCurrentPayload: hasCurrentProductQuotesPayload,
  },
  how_we_picked: {
    hasRenderablePayload: hasRenderableHowWePickedPayload,
    hasCurrentPayload: hasCurrentHowWePickedPayload,
  },
  faq: {
    hasRenderablePayload: hasRenderableFaqPayload,
    hasCurrentPayload: hasCurrentFaqPayload,
  },
  seo_title: {
    hasRenderablePayload: hasRenderableSeoTitlePayload,
    hasCurrentPayload: hasCurrentSeoTitlePayload,
  },
  meta_description: {
    hasRenderablePayload: hasRenderableMetaDescriptionPayload,
    hasCurrentPayload: hasCurrentMetaDescriptionPayload,
  },
}

function getPayloadContractState(args: {
  gate: RequiredComponentGate
  components: SeoGeneratedComponents
}) {
  const component = args.components[args.gate]
  const contract = GENERATED_COMPONENT_PAYLOAD_CONTRACTS[args.gate]
  const hasRenderablePayload = contract.hasRenderablePayload(
    component,
    args.components
  )
  const hasCurrentPayload = contract.hasCurrentPayload(
    component,
    args.components
  )

  return {
    hasRenderablePayload,
    hasCurrentPayload,
    needsUpgrade: hasRenderablePayload && !hasCurrentPayload,
  }
}

function pushUnique<T>(items: T[], item: T) {
  if (!items.includes(item)) items.push(item)
}

function isGateAttentionItem(
  readiness: SeoReportPublicationReadiness,
  gate: SeoReportPublicationGate
): boolean {
  return (
    readiness.failedGates.includes(gate) ||
    readiness.processingGates.includes(gate) ||
    readiness.missingGates.includes(gate)
  )
}

export function summarizeSeoPublicationReadiness(
  readiness: SeoReportPublicationReadiness
): string {
  if (readiness.state === 'publishable') {
    if (readiness.upgradeGates.length > 0) {
      return `Report is live with generated component upgrades available: ${readiness.upgradeGates
        .map((gate) => PUBLICATION_GATE_LABELS[gate])
        .join(', ')}.`
    }
    return 'Report is fully generated and ready to publish.'
  }

  const parts: string[] = []
  const hasFailedGates = readiness.failedGates.length > 0
  const hasProcessingGates = readiness.processingGates.length > 0
  const hasMissingGates = readiness.missingGates.length > 0
  const heroImageBlockedByImageCheck =
    readiness.failedGates.includes('image_check') &&
    isGateAttentionItem(readiness, 'hero_image')

  if (hasFailedGates) {
    const rootFailedGates = heroImageBlockedByImageCheck
      ? readiness.failedGates.filter((gate) => gate !== 'hero_image')
      : readiness.failedGates

    const blockedSuffix = heroImageBlockedByImageCheck
      ? ' - Hero image is blocked'
      : ''

    parts.push(
      `Failed: ${rootFailedGates
        .map((gate) => PUBLICATION_GATE_LABELS[gate])
        .join(', ')}${blockedSuffix}.`
    )
  }
  if (!hasFailedGates && hasProcessingGates) {
    parts.push(
      `Still processing: ${readiness.processingGates
        .map((gate) => PUBLICATION_GATE_LABELS[gate])
        .join(', ')}.`
    )
  }
  if (!hasFailedGates && !hasProcessingGates && hasMissingGates) {
    parts.push(
      `Missing: ${readiness.missingGates
        .map((gate) => PUBLICATION_GATE_LABELS[gate])
        .join(', ')}.`
    )
  }

  return parts.join(' ') || 'Report is not ready to publish.'
}

export function deriveSeoReportPublicationReadiness(args: {
  status: ResearchReportStatus
  visibility?: string | null
  updatedAt: string | Date | null | undefined
  documentPayload?: unknown
  categorySlug?: string | null
  generatedComponents?: SeoGeneratedComponents | null
  now?: number
}): SeoReportPublicationReadiness {
  const processingGates: SeoReportPublicationGate[] = []
  const failedGates: SeoReportPublicationGate[] = []
  const missingGates: SeoReportPublicationGate[] = []
  const upgradeGates: SeoReportPublicationGate[] = []
  const reasons: string[] = []

  const staleProcessing = isSeoReportProcessingStale({
    status: args.status,
    updatedAt: args.updatedAt,
    now: args.now,
  })

  const hasDocumentPayload = Boolean(args.documentPayload)

  if ((args.status === 'failed' || staleProcessing) && !hasDocumentPayload) {
    failedGates.push('document')
    reasons.push(
      staleProcessing && isSeoReportProcessingStatus(args.status)
        ? getSeoReportStaleProcessingMessage(args.status)
        : 'Report generation failed.'
    )
  } else if (isSeoReportProcessingStatus(args.status) && !hasDocumentPayload) {
    processingGates.push('document')
  }

  if (!hasDocumentPayload && !isSeoReportProcessingStatus(args.status)) {
    missingGates.push('document')
    reasons.push('Report document is missing.')
  }

  const components = args.generatedComponents ?? {}
  const useRenderablePayloads =
    args.status === 'published' && args.visibility === 'public'
  const categoryStatus = getEffectiveSeoGeneratedComponentArtifactStatus({
    component: components.category_classification,
    fallbackUpdatedAt: args.updatedAt,
    now: args.now,
    staleWhenProcessingWithoutTimestamp: useRenderablePayloads,
  })
  const categorySlug =
    args.categorySlug ??
    components.category_classification?.categorySlug ??
    (typeof args.documentPayload === 'object' &&
    args.documentPayload != null &&
    'meta' in args.documentPayload &&
    typeof (args.documentPayload as { meta?: { categorySlug?: unknown } }).meta
      ?.categorySlug === 'string'
      ? (args.documentPayload as { meta: { categorySlug: string } }).meta
          .categorySlug
      : null)

  if (!hasText(categorySlug)) {
    if (categoryStatus === 'queued' || categoryStatus === 'generating') {
      pushUnique(processingGates, 'category')
    } else if (categoryStatus === 'failed') {
      pushUnique(failedGates, 'category')
      reasons.push(
        components.category_classification?.lastError ??
          'Category classification failed.'
      )
    } else {
      pushUnique(missingGates, 'category')
      reasons.push('Category is missing.')
    }
  }

  for (const gate of REQUIRED_COMPONENT_GATES) {
    const component = components[gate]
    const effectiveStatus = getEffectiveSeoGeneratedComponentArtifactStatus({
      component,
      fallbackUpdatedAt: args.updatedAt,
      now: args.now,
      staleWhenProcessingWithoutTimestamp: useRenderablePayloads,
    })
    const payloadState = getPayloadContractState({ gate, components })

    if (useRenderablePayloads && payloadState.hasRenderablePayload) {
      if (payloadState.needsUpgrade) {
        upgradeGates.push(gate)
      }
      continue
    }

    if (effectiveStatus === 'queued' || effectiveStatus === 'generating') {
      processingGates.push(gate)
      continue
    }

    if (effectiveStatus === 'failed') {
      failedGates.push(gate)
      const lastError =
        component && 'lastError' in component ? component.lastError : null
      reasons.push(
        hasText(lastError)
          ? lastError
          : `${PUBLICATION_GATE_LABELS[gate]} generation failed.`
      )
      continue
    }

    if (!payloadState.hasCurrentPayload) {
      missingGates.push(gate)
      reasons.push(`${PUBLICATION_GATE_LABELS[gate]} is missing.`)
    }
  }

  const state: SeoReportPublicationReadinessState =
    failedGates.length > 0
      ? 'needs_attention'
      : processingGates.length > 0
        ? 'processing'
        : missingGates.length > 0
          ? 'needs_attention'
          : 'publishable'

  return {
    state,
    processingGates,
    failedGates,
    missingGates,
    upgradeGates,
    reasons,
  }
}

export function deriveSeoReportDisplayModel(args: {
  status: ResearchReportStatus
  visibility: string
  updatedAt: string | Date | null | undefined
  documentPayload?: unknown
  categorySlug?: string | null
  generatedComponents?: SeoGeneratedComponents | null
  now?: number
}): SeoReportDisplayModel {
  const {
    status,
    visibility,
    updatedAt,
    documentPayload,
    categorySlug,
    generatedComponents,
    now,
  } = args
  const staleProcessing = isSeoReportProcessingStale({
    status,
    updatedAt,
    now,
  })

  const components = REQUIRED_COMPONENT_GATES.map(
    (gate) => generatedComponents?.[gate]
  ).filter(Boolean)

  // Apply stale detection to generated content components before deriving
  // admin-facing issue flags. Category classification is a publication gate,
  // but a stale marker there should not surface as a component issue when the
  // persisted category slug already satisfies readiness.
  const staleWhenProcessingWithoutTimestamp =
    status === 'published' && visibility === 'public'
  const effectiveComponentStatuses = components.map((c) =>
    getEffectiveSeoGeneratedComponentArtifactStatus({
      component: c,
      fallbackUpdatedAt: updatedAt,
      now,
      staleWhenProcessingWithoutTimestamp,
    })
  )

  const isEnriching = effectiveComponentStatuses.some(
    (s) => s === 'queued' || s === 'generating'
  )
  const hasComponentFailures = effectiveComponentStatuses.some(
    (s) => s === 'failed'
  )
  const readiness =
    status === 'published' && visibility === 'public'
      ? deriveSeoReportPublicationReadiness({
          status,
          visibility,
          updatedAt,
          documentPayload,
          categorySlug,
          generatedComponents,
          now,
        })
      : null

  let primary: SeoReportDisplayStatus

  if (status === 'archived') {
    primary = 'archived'
  } else if (status === 'failed' || staleProcessing) {
    primary = 'failed'
  } else if (status === 'queued' || status === 'generating') {
    primary = 'processing'
  } else if (readiness?.state === 'processing') {
    primary = 'processing'
  } else if (readiness?.state === 'needs_attention') {
    primary = 'failed'
  } else if (status === 'published' && visibility === 'public') {
    primary = 'live'
  } else {
    // draft, ready, or published-but-internal
    primary = 'draft'
  }

  return {
    primary,
    isEnriching,
    hasComponentFailures,
    staleProcessing,
  }
}
